import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "../lib/api";
import axios from "axios";
import { Anvil, Phone, MapPin, ArrowRight, Loader2 } from "lucide-react";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export default function PublicPage() {
  const { companyId, slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/site/${companyId}/page/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [companyId, slug]);

  useEffect(() => {
    if (!data) return;
    const seo = data.page.seo || {};
    document.title = seo.meta_title || data.page.title;
    setMeta("description", seo.meta_description);
    setMeta("keywords", (seo.keywords || []).join(", "));
    setMeta("geo.region", seo.geo_region);
    setMeta("geo.placename", seo.geo_placename);
    setMeta("og:title", seo.meta_title || data.page.title, "property");
    setMeta("og:description", seo.meta_description, "property");
  }, [data]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-500">Page introuvable.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>;

  const { page, company } = data;
  const seo = page.seo || {};

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-sky-400 flex items-center justify-center"><Anvil className="w-5 h-5 text-navy" /></div>
            <span className="font-display font-bold text-lg">{company.name}</span>
          </div>
          {(seo.geo_city || seo.geo_region) && (
            <div className="hidden sm:flex items-center gap-1.5 text-sm text-slate-300">
              <MapPin className="w-4 h-4 text-sky-400" /> {[seo.geo_city, seo.geo_region].filter(Boolean).join(", ")}
            </div>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-navy-deep text-white overflow-hidden">
        {page.hero_image && (
          <div className="absolute inset-0">
            <img src={page.hero_image} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/70 to-navy-deep/40" />
          </div>
        )}
        <div className="relative max-w-5xl mx-auto px-5 py-20 sm:py-28">
          <h1 className="text-4xl sm:text-5xl font-display font-bold leading-tight max-w-3xl">{page.hero_title || page.title}</h1>
          {page.hero_subtitle && <p className="mt-5 text-lg text-slate-300 max-w-2xl">{page.hero_subtitle}</p>}
          <a href="tel:" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-sky-400 text-navy font-semibold hover:bg-sky-500 transition-colors">
            <Phone className="w-4 h-4" /> Demander un devis gratuit
          </a>
        </div>
      </section>

      {/* Sections */}
      <main className="max-w-5xl mx-auto px-5 py-16 space-y-14">
        {(page.sections || []).map((s, i) => (
          <section key={i} className={`grid gap-8 items-center ${s.image ? "md:grid-cols-2" : "grid-cols-1"}`}>
            <div>
              {s.heading && <h2 className="text-2xl sm:text-3xl font-display font-bold text-navy">{s.heading}</h2>}
              {s.body && <p className="mt-4 text-slate-600 leading-relaxed whitespace-pre-wrap">{s.body}</p>}
            </div>
            {s.image && <img src={s.image} alt={s.heading} className="rounded-2xl w-full object-cover" />}
          </section>
        ))}

        {seo.keywords?.length > 0 && (
          <section className="pt-6 border-t border-slate-100">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Nos spécialités</h3>
            <div className="flex flex-wrap gap-2 mt-3">
              {seo.keywords.map((k, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-sm">{k}</span>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-navy text-slate-300">
        <div className="max-w-5xl mx-auto px-5 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <div className="font-display font-bold text-white text-lg">{company.name}</div>
            {seo.geo_placename && <p className="text-sm text-slate-400 mt-1">{seo.geo_placename}</p>}
          </div>
          <Link to="/login" className="inline-flex items-center gap-2 text-sky-400 text-sm hover:underline">
            Propulsé par Fabriko <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </footer>
    </div>
  );
}
